export const MAX_NUMBER_INPUT_DIGITS = 10;
export const MAX_QUANTITY_INPUT_DIGITS = 4;

export const sanitizeQuantityInputValue = (value) =>
  String(value ?? "")
    .replace(/\D/g, "")
    .slice(0, MAX_QUANTITY_INPUT_DIGITS);

const numberInput = (target) =>
  target instanceof HTMLInputElement &&
  (target.type === "number" || target.inputMode === "decimal");

const digitCount = (value) => (String(value).match(/\d/g) || []).length;

export const isPermittedNumberInsertion = (
  currentValue,
  insertedText,
  selectionStart = currentValue.length,
  selectionEnd = selectionStart
) => {
  const nextValue = `${currentValue.slice(
    0,
    selectionStart
  )}${insertedText}${currentValue.slice(selectionEnd)}`;

  return (
    !/[a-z]/i.test(insertedText) &&
    /^[+-]?(?:\d+\.?\d*|\.\d*)?$/.test(nextValue) &&
    digitCount(nextValue) <= MAX_NUMBER_INPUT_DIGITS
  );
};

export const sanitizeNumberInputValue = (value) => {
  const source = String(value);
  const sign = /^[+-]/.test(source) ? source[0] : "";
  let digits = 0;
  let decimalUsed = false;
  let result = sign;

  for (const character of source.slice(sign ? 1 : 0)) {
    if (/\d/.test(character)) {
      if (digits < MAX_NUMBER_INPUT_DIGITS) {
        result += character;
        digits += 1;
      }
      continue;
    }

    if (character === "." && !decimalUsed && digits < MAX_NUMBER_INPUT_DIGITS) {
      result += character;
      decimalUsed = true;
    }
  }

  return result;
};

const setNativeInputValue = (input, value) => {
  const setter = Object.getOwnPropertyDescriptor(
    HTMLInputElement.prototype,
    "value"
  )?.set;

  if (setter) setter.call(input, value);
  else input.value = value;
};

const blockInvalidKey = (event) => {
  if (
    !numberInput(event.target) ||
    event.ctrlKey ||
    event.metaKey ||
    event.altKey
  )
    return;

  if (event.key.length !== 1) return;

  if (/[a-z]/i.test(event.key)) {
    event.preventDefault();
  }
};

const blockInvalidBeforeInput = (event) => {
  if (!numberInput(event.target) || !event.inputType?.startsWith("insert"))
    return;

  if (event.data !== null && /[a-z]/i.test(event.data)) {
    event.preventDefault();
  }
};

const blockInvalidPaste = (event) => {
  if (!numberInput(event.target)) return;
  const pastedText = event.clipboardData?.getData("text");
  if (pastedText === undefined) return;

  if (/[a-z]/i.test(pastedText)) {
    event.preventDefault();
  }
};

const sanitizeInput = (event) => {
  if (!numberInput(event.target)) return;
  const sanitized = sanitizeNumberInputValue(event.target.value);
  if (sanitized !== event.target.value)
    setNativeInputValue(event.target, sanitized);
};

export function installNumberInputConstraints(root = document) {
  root.addEventListener("keydown", blockInvalidKey, true);
  root.addEventListener("beforeinput", blockInvalidBeforeInput, true);
  root.addEventListener("paste", blockInvalidPaste, true);
  root.addEventListener("input", sanitizeInput, true);

  return () => {
    root.removeEventListener("keydown", blockInvalidKey, true);
    root.removeEventListener("beforeinput", blockInvalidBeforeInput, true);
    root.removeEventListener("paste", blockInvalidPaste, true);
    root.removeEventListener("input", sanitizeInput, true);
  };
}
